import React, { useState, useRef, useEffect } from "react";
import { useForm } from "../FormContext";
import type { SliderFieldProps } from "../../../interfaces/components/form";

export const RangeField: React.FC<SliderFieldProps> = ({
  name,
  value,
  onChange,
  onBlur,
  min = 0,
  max = 100,
  step = 1,
  className = "",
  color = "primary",
  showValue = true,
  showMinMax = false,
  disabled = false,
}) => {
  const { setFieldValue, setFieldTouched, validateField, fields } = useForm();
  const fieldValue = fields[name]?.value ?? value ?? min;
  const fieldErrors = fields[name]?.errors || [];
  const hasError = fieldErrors.length > 0;

  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef<HTMLDivElement>(null);

  const colorClasses = {
    primary: "bg-primary",
    blue: "bg-blue-500",
    green: "bg-green-500",
    purple: "bg-purple-500",
    red: "bg-red-500",
    orange: "bg-orange-500",
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragging(true);
    updateValue(e);
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDragging || disabled) return;
    e.preventDefault();
    updateValue(e);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setFieldTouched(name, true);
    validateField(name);
    if (onBlur) {
      onBlur();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (disabled) return;

    let newValue = Number(fieldValue);
    switch (e.key) {
      case "ArrowLeft":
      case "ArrowDown":
        e.preventDefault();
        newValue = Math.max(min, newValue - step);
        break;
      case "ArrowRight":
      case "ArrowUp":
        e.preventDefault();
        newValue = Math.min(max, newValue + step);
        break;
      case "Home":
        e.preventDefault();
        newValue = min;
        break;
      case "End":
        e.preventDefault();
        newValue = max;
        break;
      default:
        return;
    }

    setFieldValue(name, newValue);
    if (onChange) {
      onChange(newValue);
    }

    // Validate on keyboard change
    setTimeout(() => {
      setFieldTouched(name, true);
      validateField(name);
    }, 0);
  };

  const updateValue = (e: MouseEvent | React.MouseEvent) => {
    if (!sliderRef.current) return;

    const rect = sliderRef.current.getBoundingClientRect();
    const percentage = Math.max(
      0,
      Math.min(1, (e.clientX - rect.left) / rect.width),
    );
    const rawValue = min + percentage * (max - min);
    const steppedValue = Math.round(rawValue / step) * step;
    const clampedValue = Math.max(min, Math.min(max, steppedValue));

    setFieldValue(name, clampedValue);
    if (onChange) {
      onChange(clampedValue);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => handleMouseMove(e);
    const handleGlobalMouseUp = () => handleMouseUp();

    if (isDragging) {
      document.addEventListener("mousemove", handleGlobalMouseMove, {
        passive: false,
      });
      document.addEventListener("mouseup", handleGlobalMouseUp);
      document.body.style.userSelect = "none";

      return () => {
        document.removeEventListener("mousemove", handleGlobalMouseMove);
        document.removeEventListener("mouseup", handleGlobalMouseUp);
        document.body.style.userSelect = "";
      };
    }
  }, [isDragging]);

  const percentage = ((Number(fieldValue) - min) / (max - min)) * 100;

  return (
    <div className={`w-full ${className}`}>
      <div className="relative">
        <div
          ref={sliderRef}
          className={`relative h-2 bg-gray-200 dark:bg-gray-700 rounded-full cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary ${
            disabled
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-gray-300 dark:hover:bg-gray-600"
          }`}
          onMouseDown={handleMouseDown}
          onKeyDown={handleKeyDown}
          tabIndex={disabled ? -1 : 0}
          role="slider"
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={Number(fieldValue)}
          aria-label={name}
        >
          {/* Progress bar */}
          <div
            className={`absolute top-0 left-0 h-full rounded-full ${
              isDragging ? "" : "transition-all duration-100 ease-out"
            } ${
              hasError
                ? "bg-red-500"
                : colorClasses[color] || colorClasses.primary
            }`}
            style={{ width: `${percentage}%` }}
          />

          {/* Thumb */}
          <div
            className={`absolute top-1/2 w-5 h-5 -mt-2.5 -ml-2.5 rounded-full border-2 border-white ${
              isDragging
                ? "scale-110"
                : "transition-transform duration-100 ease-out hover:scale-105"
            } ${
              hasError
                ? "bg-red-500"
                : colorClasses[color] || colorClasses.primary
            } ${
              disabled
                ? "cursor-not-allowed"
                : "cursor-grab active:cursor-grabbing"
            }`}
            style={{ left: `${percentage}%` }}
          />
        </div>

        {(showValue || showMinMax) && (
          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mt-2">
            {showMinMax && <span>{min}</span>}
            {showValue && (
              <span
                className={`font-medium ${hasError ? "text-red-500" : "text-gray-700 dark:text-gray-300"}`}
              >
                {Number(fieldValue).toFixed(step < 1 ? 1 : 0)}
              </span>
            )}
            {showMinMax && <span>{max}</span>}
          </div>
        )}
      </div>
    </div>
  );
};
